'use client'

import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ImageKind } from '@/shared/types/media/image-kind'
import { pluralize } from '@/shared/utilities/pluralizer.utility'
import { uploadImage } from './actions'
import type { UploadedImage } from './types'
import { hiveFeedKeys } from '@/features/post/hooks/use-hive-feed'

export interface QueuedPhoto {
  id: string
  file: File
  caption: string
}

export interface UploadBatch {
  batchId: string
  /** Optional businessId — when set, the toast names the business and the
   *  business-photos query is invalidated on completion. */
  businessId?: string
  businessName?: string
  /** Optional hiveId — for invalidating hive feeds when the photos are
   *  attached to a hive post (Phase 3 wiring). */
  hiveId?: string
  photos: QueuedPhoto[]
}

export interface UploadBatchResult {
  uploaded: UploadedImage[]
  failed: number
  total: number
}

interface BackgroundUploaderContextValue {
  /**
   * Kick off a batch upload. Resolves once every photo has been attempted
   * (success or failure). Caller can pass `onShareInHive` to surface a
   * "Share in a hive" action on the success toast.
   */
  startBatch: (
    batch: UploadBatch,
    onShareInHive?: (uploaded: UploadedImage[]) => void
  ) => Promise<UploadBatchResult>
}

const BackgroundUploaderContext = createContext<BackgroundUploaderContextValue | null>(null)

const CONCURRENCY = 3

export function useBackgroundUploader(): BackgroundUploaderContextValue {
  const ctx = useContext(BackgroundUploaderContext)
  if (!ctx) {
    throw new Error('useBackgroundUploader must be used inside <BackgroundUploaderProvider />')
  }
  return ctx
}

export function BackgroundUploaderProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const inflight = useRef<Set<string>>(new Set())

  const startBatch = useCallback<BackgroundUploaderContextValue['startBatch']>(
    async (batch, onShareInHive) => {
      if (inflight.current.has(batch.batchId)) {
        return { uploaded: [], failed: 0, total: 0 }
      }
      inflight.current.add(batch.batchId)

      const total = batch.photos.length
      const toastId = `upload-${batch.batchId}`
      let completed = 0

      const renderProgress = () => {
        toast.loading(
          `Uploading ${completed} of ${total} ${pluralize('photo', total)}…`,
          { id: toastId }
        )
      }
      renderProgress()

      // First reason a photo was rejected — surfaced on the failure toast so the
      // user learns why (format, size) instead of just "failed".
      let firstError: string | null = null

      const uploadOne = async (photo: QueuedPhoto): Promise<UploadedImage | null> => {
        try {
          const formData = new FormData()
          formData.append('file', photo.file)
          if (photo.caption) {
            formData.append('caption', photo.caption)
          }
          if (batch.businessId) {
            formData.append('business_id', batch.businessId)
          }
          if (batch.hiveId) {
            formData.append('hive_id', batch.hiveId)
          }
          const result = await uploadImage(
            formData,
            batch.businessId ? ImageKind.BUSINESS : ImageKind.POST
          )
          if (!result.success) {
            firstError ??= result.error.userMessage
            return null
          }
          completed += 1
          renderProgress()
          return result.data
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[BackgroundUploader] photo upload failed', e)
          return null
        }
      }

      const queue = [...batch.photos]
      const results: (UploadedImage | null)[] = []
      const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
        while (queue.length) {
          const next = queue.shift()
          if (!next) break
          results.push(await uploadOne(next))
        }
      })
      await Promise.all(workers)

      const uploaded = results.filter((r): r is UploadedImage => r !== null)
      const failed = total - uploaded.length

      if (batch.businessId) {
        queryClient.invalidateQueries({ queryKey: ['business-photos', batch.businessId] })
      }
      if (batch.hiveId) {
        queryClient.invalidateQueries({ queryKey: hiveFeedKeys.byHive(batch.hiveId) })
      }

      if (uploaded.length === 0) {
        toast.error(firstError ?? 'Upload failed', { id: toastId })
      } else {
        const targetLabel = batch.businessName ? ` to ${batch.businessName}` : ''
        toast.success(
          `${uploaded.length} ${pluralize('photo', uploaded.length)} added${targetLabel}${
            failed ? ` (${failed} failed)` : ''
          }`,
          {
            id: toastId,
            duration: 8000,
            action: onShareInHive
              ? {
                  label: 'Share in a hive',
                  onClick: () => onShareInHive(uploaded),
                }
              : undefined,
          }
        )
      }

      inflight.current.delete(batch.batchId)
      return { uploaded, failed, total }
    },
    [queryClient]
  )

  return (
    <BackgroundUploaderContext.Provider value={{ startBatch }}>
      {children}
    </BackgroundUploaderContext.Provider>
  )
}
